import { Pagination } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Card,
  Checkbox,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridColumnVisibilityModel,
  GridColumns,
  GridFooterContainer,
  GridPagination,
  GridRowIdGetter,
  GridRowParams,
  GridToolbar,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppGridId, GridIdsToSave } from "../../models/app/AppGridModels";
import {
  addGridSetting,
  useAddAppGridSettingMutation,
} from "../../redux/reducer/features/App/AppGridSlice";
import { AppState } from "../../redux/reducer/rootReducer";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { DmsManufacturer } from "../../models/features/ShoppingPortal/DmsManufacturer";

interface ServerAppGridProps {
  columns: GridColumns<any>;
  isLoading: boolean;
  data: any[];
  height?: any;
  onSelect?: any;
  selectedRows?: any;
  getRowId?: GridRowIdGetter<any>;
  setPaging: any;
  paging: any;
  totalRecords: number;
  hideFooter?: boolean;
  hidePaginator?: boolean;
  disableSelection?: boolean;
  keepNonExistentRowsSelected?: boolean | undefined;
  hideColumnFilter?: boolean;
  hideCheckBoxes?: boolean;
  hidePaginationText?: boolean;
  hidePageSize?: boolean;
  gridId?: AppGridId | undefined;
  isRowSelectable?: ((params: GridRowParams<any>) => boolean) | undefined;
  customToolbar?: boolean;
  switchLabel?: string;
  switchChecked?: boolean;
  onSwitchChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  manufacturerOptions?: DmsManufacturer[];
  selectedManufacturers?: DmsManufacturer[];
  onManufacturerChange?: (selected: DmsManufacturer[]) => void;
}

const CustomToolbar = ({
  switchLabel,
  switchChecked,
  onSwitchChange,
  manufacturerOptions,
  selectedManufacturers,
  onManufacturerChange,
}: {
  switchLabel: string;
  switchChecked: boolean;
  onSwitchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  manufacturerOptions?: DmsManufacturer[];
  selectedManufacturers?: DmsManufacturer[];
  onManufacturerChange?: (selected: DmsManufacturer[]) => void;
}) => {
  const allOption: DmsManufacturer = {
    manufacturerId: "all",
    code: "",
    name: "Select All",
    isActive: true,
  };
  const allSelected =
    (manufacturerOptions?.length ?? 0) > 0 &&
    (selectedManufacturers?.length ?? 0) === (manufacturerOptions?.length ?? 0);

  return (
    <GridToolbarContainer>
      <Box display={"flex"} justifyContent={"space-between"} width="100%">
        <Box>
          <GridToolbarColumnsButton {...({} as any)} />
          <GridToolbarDensitySelector {...({} as any)} />
          <GridToolbarExport {...({} as any)} />
        </Box>
        <Box display={"flex"} gap={3}>
          <Box width={300}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={[allOption, ...(manufacturerOptions || [])]}
              value={selectedManufacturers}
              onChange={(event, value) => {
                if (value.some((v) => v.manufacturerId === "all")) {
                  if (!allSelected) {
                    onManufacturerChange?.(manufacturerOptions || []);
                  } else {
                    onManufacturerChange?.([]);
                  }
                } else {
                  onManufacturerChange?.(value);
                }
              }}
              getOptionLabel={(option) => option.name}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="medium" />}
                    checkedIcon={<CheckBoxIcon fontSize="medium" />}
                    style={{ marginRight: 5, padding: 0 }}
                    checked={
                      option.manufacturerId === "all" ? allSelected : selected
                    }
                  />
                  {option.name}
                </li>
              )}
              style={{ width: 300 }}
              renderInput={(params) => (
                <TextField {...params} label="Manufacturers" size="small" />
              )}
              renderTags={() => null}
            />
          </Box>
          <FormControlLabel
            checked={switchChecked}
            control={
              <Switch
                name="jason"
                value={switchChecked}
                onChange={onSwitchChange}
                size="small"
              />
            }
            label={switchLabel}
          />
          <GridToolbarQuickFilter />
        </Box>
      </Box>
    </GridToolbarContainer>
  );
};

const ServerAppGrid: React.FunctionComponent<ServerAppGridProps> = (props) => {
  const { userId } = useSelector((state: AppState) => state.authReducer);
  const dispatch = useDispatch();
  const [saveSettingMutation, settingMutationResult] =
    useAddAppGridSettingMutation();

  const handleSetVisibilityModel = (model: GridColumnVisibilityModel) => {
    if (props.gridId !== undefined) {
      dispatch(
        addGridSetting({
          setting: { gridId: props.gridId, columnVisibilityModel: model },
        }),
      );
      if (GridIdsToSave.includes(props.gridId)) {
        var data = JSON.stringify(model);
        saveSettingMutation({
          data: { userId: userId ?? "", gridId: props.gridId, setting: data },
        });
      }
    }
  };
  function CustomPagination() {
    const apiRef = useGridApiContext();
    const page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <>
        <label></label>
        <Pagination
          color="primary"
          count={pageCount}
          page={page + 1}
          onChange={(event, value) => apiRef.current.setPage(value - 1)}
        />
      </>
    );
  }
  function CustomFooter() {
    return (
      <GridFooterContainer sx={{ width: props.hidePaginator ? "auto" : "80%" }}>
        {props.hidePaginator ? <></> : <CustomPagination />}
        <GridPagination {...({} as any)} />
      </GridFooterContainer>
    );
  }
  const handleGetColumns = (): GridColumns<any> => {
    return props.columns.map((col) =>
      col.sortable ? col : { ...col, sortable: false },
    );
  };

  //This code handles page size pagination correctly when pageSize go beyond 100, which is not allowed in MUI datagrid.
  useEffect(() => {
    const handleClick = (e: Event) => {
      const target = e.target as HTMLElement;
      const li = target.closest("li[data-value]") as HTMLLIElement | null;
      if (li) {
        const newPageSize = Number(li.dataset.value);
        props.setPaging((prev: any) => ({
          ...prev,
          pageSize: newPageSize,
          pageIndex: 0,
        }));
      }
    };
    document.addEventListener("click", handleClick);
    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, []);
  return (
    <>
      <Card>
        <Box sx={{ height: props.height ?? 620, width: "100%" }}>
          <DataGrid
            className={`${
              props.hidePaginationText ? "hide-pagination-text" : " "
            }`}
            getRowId={props.getRowId}
            rows={props.data}
            columns={handleGetColumns()}
            rowsPerPageOptions={
              props.hidePageSize
                ? [10, 20, 50, 100, 500, 1000]
                : [10, 20, 50, 100]
            }
            pagination
            paginationMode="server"
            onPageChange={(newPage) => {
              props.setPaging({
                ...props.paging,
                pageIndex: newPage,
              });
            }}
            //Commenting out , because handled explicity through JS due to cusome pageSize : >100
            // onPageSizeChange={(model) => {
            //   props.setPaging({
            //     ...props.paging,
            //     pageSize: model,
            //   });
            // }}
            filterMode="server"
            page={props.paging.pageIndex}
            selectionModel={props.selectedRows}
            pageSize={props.hidePageSize ? undefined : props.paging.pageSize}
            hideFooter={props.hideFooter ? true : false}
            filterModel={props.paging}
            onFilterModelChange={(model) => {
              const quickFiltersChanged =
                JSON.stringify(props.paging.quickFilterValues) !==
                JSON.stringify(model.quickFilterValues);
              props.setPaging({
                ...props.paging,
                pageIndex: quickFiltersChanged ? 0 : props.paging.pageIndex,
                items: model.items.map((n) => ({
                  columnField: n.columnField,
                  operatorValue: n.operatorValue ?? "",
                  value: n.value,
                })),
                quickFilterValues: model.quickFilterValues ?? [],
              });
            }}
            onSortModelChange={(model) => {
              props.setPaging({
                ...props.paging,
                pageIndex: 0,
                sorting: model.length > 0 ? model[0] : null,
              });
            }}
            rowCount={props.totalRecords}
            checkboxSelection={props.hideCheckBoxes ? false : true}
            disableSelectionOnClick
            loading={props.isLoading}
            onSelectionModelChange={(newSelection: any, detail: any) => {
              props.onSelect && props.onSelect(newSelection);
            }}
            localeText={{
              footerRowSelected: (count) =>
                count !== 1
                  ? `${count.toLocaleString()} rows selected`
                  : `${count.toLocaleString()} row selected`,
            }}
            components={{
              Toolbar: props.customToolbar ? CustomToolbar : GridToolbar,
              Pagination: CustomFooter,
            }}
            componentsProps={{
              toolbar: props.customToolbar
                ? {
                    switchLabel: props.switchLabel,
                    switchChecked: props.switchChecked,
                    onSwitchChange: props.onSwitchChange,
                    manufacturerOptions: props.manufacturerOptions,
                    selectedManufacturers: props.selectedManufacturers,
                    onManufacturerChange: props.onManufacturerChange,
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 },
                  }
                : {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 },
                  },
            }}
            isRowSelectable={props.isRowSelectable}
            keepNonExistentRowsSelected={
              props.keepNonExistentRowsSelected ?? true
            }
            disableColumnFilter={props.hideColumnFilter}
            onColumnVisibilityModelChange={handleSetVisibilityModel}
          />
        </Box>
      </Card>
    </>
  );
};

export default ServerAppGrid;
